import { Suspense, lazy } from 'preact/compat';
import { Route, Routes, useParams, useSearchParams } from "react-router-dom";
import { MenuProvider } from "@/context/MenuContext";
import { WishlistContext } from "@/context/WishlistContext";
import useWishList from "@/hooks/useWishList";
import useRetailerAddress from '@/hooks/useRetailerAddress';
import useLocalStorageState from "use-local-storage-state";

const AllProducts = lazy(() => import("@/pages/AllProducts"));
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const BrandPage = lazy(() => import("@/pages/BrandPage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const WishList = lazy(() => import("@/pages/WishList"));

const SpecialsPage = lazy(() => import("@/pages/Specials"));
const Special = lazy(() => import("@/pages/Special"));
const RetailerPage = lazy(() => import("@/pages/RetailerPage"));
const AgeGate = lazy(() =>  import("@/components/AgeGate"));
import OrderConfirmation from "@/pages/OrderConfirmation";
import LocationSelector from "@/components/LocationSelector";
import LocationToggle from "@/components/LocationToggle";
import NavActions from "@/components/NavActions";
import SideCart from "@/components/SideCart";
import SnackBar from "@/components/SnackBar";
import WeightModal from "@/components/WeightModal";
import ProductSlider from "@/components/ProductSlider";
import TabbedProductSlider from '@/components/TabbedProductSlider';
import FeaturedProduct from '@/components/blog/FeaturedProduct';
import useSpecials from '@/hooks/useSpecials';
import LocationCards from '@/LocationCards';

function App() {
	useRetailerAddress();
	const [ageGatePassed, setAgeGatePassed] = useLocalStorageState("cp_age_gate_passed", false);
	const sliders = typeof product_sliders !== 'undefined' ? product_sliders : [];
	const tabbedSliders = typeof tabbed_product_sliders !== 'undefined' ? tabbed_product_sliders : [];
	const featuredProductsNodes = document.querySelectorAll('.featured-product-dutchie');
	const featuredProducts = Array.from(featuredProductsNodes);

	return (
		<div className="app">
			{!ageGatePassed && (
				<Suspense fallback={null}>
					<AgeGate />
				</Suspense>
			)}
			<LocationToggle />
			<LocationSelector />
			<NavActions />
			<SideCart />
			<SnackBar />
			<WeightModal />
			{ sliders && sliders.length > 0 && <RenderSliders sliders={sliders} />}
	 		{ tabbedSliders && tabbedSliders.length > 0 && <RenderTabbedSliders sliders={tabbedSliders} />}
			{ featuredProducts && featuredProducts.length > 0 && <RenderFeaturedProducts products={featuredProducts} /> }

			<Routes>
				<Route path="/shop" element={<AllProductsWrapper />} />
				<Route path="/specials" element={<SpecialsPageWrapper />} />
				<Route path="/special" element={<SpecialWrapper />} />
				<Route path="/shop/:categorySlug" element={<CategoryPageWrapper />} />
				<Route path="/brands/:brandSlug" element={<BrandPageWrapper />} />
				<Route path="/product/:productSlug" element={<ProductDetailPageWrapper />} />
				<Route path="/wish-list" element={<WishListPageWrapper />} />
				<Route path="/order-confirmation" element={<OrderConfirmation />} />
				<Route path="/location/:locationSlug" element={<RetailerPageWrapper />} />
			</Routes>
		</div>
	)
}

function SpecialsPageWrapper() {
	return (
		<MenuProvider specialItems={true}>
			<Suspense fallback={null}>
				<SpecialsPage />
			</Suspense>
		</MenuProvider>
	);
}

function SpecialWrapper() {
	const [searchParams] = useSearchParams();
    const specialID = searchParams.get('specialID');
	const { filteredSpecials } = useSpecials();
	const special = filteredSpecials.find((special) => special.id === specialID);
	if ( !special) {
		return null;
	}
	return (
		<MenuProvider specialID={specialID} special={special}>
			<Suspense fallback={null}>
				<Special specialID={specialID} special={special} />
			</Suspense>
		</MenuProvider>
	);
}

function RetailerPageWrapper() {
	const { locationSlug } = useParams();
	return (
		<MenuProvider>
			<Suspense fallback={null}>
				<RetailerPage locationSlug={locationSlug} />
			</Suspense>
		</MenuProvider>
	);
}

function AllProductsWrapper() {
	return (
		<MenuProvider>
			<Suspense fallback={null}>
				<AllProducts />
			</Suspense>
		</MenuProvider>
	);
}

function CategoryPageWrapper() {
	const { categorySlug } = useParams();

	return (
		<MenuProvider categorySlug={categorySlug}>
			<Suspense fallback={null}>
				<CategoryPage categorySlug={categorySlug} />
			</Suspense>
		</MenuProvider>
	);
}

function BrandPageWrapper() {
	return (
		<MenuProvider>
			<Suspense fallback={null}>
				<BrandPage />
			</Suspense>
		</MenuProvider>
	);
}

function ProductDetailPageWrapper() {
	return (
		<Suspense fallback={null}>
			<ProductDetailPage />
		</Suspense>
	);
}

function WishListPageWrapper() {
	const { 
		wishList, 
		removeFromWishlist,
		addAllItemsToCart,
		buyAllItemsNow,
		calculateTotalPrice,
	} = useWishList();
	return (
		<WishlistContext.Provider value={{ wishList, removeFromWishlist, addAllItemsToCart, buyAllItemsNow, calculateTotalPrice }}>
			<Suspense fallback={null}>
				<WishList />
			</Suspense>
		</WishlistContext.Provider>
	);
}

function RenderSliders({ sliders }) {
	return sliders.map((slider, index) => (
		<ProductSlider key={index} settings={slider} />
	));
}

function RenderTabbedSliders({ sliders }) {
	return sliders.map((slider, index) => (
		<TabbedProductSlider key={index} settings={slider} />
	));
}

function RenderFeaturedProducts({ products }) {

	return (
		<>
			{products.map((product, index) => {
				const productId = product.dataset.product;
				const enterpriseProductId = product.dataset.enterpriseproduct;
				const target = product.querySelector('.featured-product-actions');
				return <FeaturedProduct key={index} productId={productId} enterpriseProductId={enterpriseProductId} target={target} />
			})}
		</>
	);
}

export default App;